/**
 * React binding for the catalogue.
 *
 * Deliberately a context rather than a module-level singleton: switching locale
 * has to re-render every string on screen, and a singleton read at import time
 * cannot do that. It also makes the locale overridable in tests without
 * touching global state.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createTranslator,
  detectLocale,
  ensureCountryNames,
  setAmbientLocale,
  type MessageKey,
  type MessageValues,
  type Translator,
} from '@vav/i18n';
import { setLocale as setFormatLocale } from './format.ts';

interface I18nContext extends Translator {
  setLocale: (locale: string) => void;
}

const Context = createContext<I18nContext | undefined>(undefined);

/**
 * `?locale=ar` forces a locale for QA.
 *
 * The brief's QA set is en, he, ar, es, pt-BR, hi and ja, and Arabic is the one
 * that matters most -- it catches bidi bugs Hebrew hides, because it also
 * shapes and joins glyphs. Changing the OS language seven times is not a
 * workflow anyone will actually follow, so the app has to be able to say it.
 */
function requestedLocale(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('locale') ?? undefined;
}

/**
 * A chosen locale outranks browser detection, and has to survive a reload —
 * otherwise the user picks their language, refreshes, and is back where they
 * started. `localStorage` rather than the encrypted store because this is a
 * display preference, and it has to be readable before the account loads.
 */
const STORED_LOCALE = 'vav.locale';

function storedLocale(): string | undefined {
  try {
    return window.localStorage.getItem(STORED_LOCALE) ?? undefined;
  } catch {
    // Private-mode Safari and some embedded webviews throw here rather than
    // returning null. A missing preference is not worth failing a render over.
    return undefined;
  }
}

export function I18nProvider({ children, locale }: { children: ReactNode; locale?: string }) {
  const [active, setActive] = useState(
    () => locale ?? requestedLocale() ?? storedLocale() ?? detectLocale(),
  );

  // A no-op in every current browser, which all ship Intl.DisplayNames. Kept so
  // the two clients take the same path and an older engine degrades the same way.
  useEffect(() => {
    void ensureCountryNames();
  }, []);

  const value = useMemo<I18nContext>(() => {
    const translator = createTranslator(active);
    return {
      ...translator,
      setLocale(next: string) {
        // Dates and numbers come from `format.ts`, which holds its own memoised
        // Intl instances. They have to move together or the app renders Hebrew
        // text with English timestamps.
        setFormatLocale(next);
        setAmbientLocale(next);
        try {
          window.localStorage.setItem(STORED_LOCALE, next);
        } catch {
          // Preference is lost on reload, but the app still works this session.
        }
        setActive(next);
      },
    };
  }, [active]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useI18n(): I18nContext {
  const context = useContext(Context);
  if (!context) throw new Error('useI18n used outside I18nProvider');
  return context;
}

/** The common case: just the translate function. */
export function useT(): (key: MessageKey, values?: MessageValues) => string {
  return useI18n().t;
}
